import React, { useState } from 'react';
import { ChevronDown, ChevronUp, User, Trophy, DollarSign, Target } from 'lucide-react';
import { Card } from './ui';

/**
 * LeaderboardCard - Mobile-optimized player card for season leaderboard
 *
 * "Performance Lounge" Design:
 * - High contrast for outdoor/bright light visibility
 * - Premium feel with subtle gradients and shadows
 * - Expandable for detailed stats
 * - Gold accents for top performers
 */
const LeaderboardCard = ({
  player,
  rank,
  isTop8,
  onPlayerClick,
  getPlayerImageUrl,
  sortKey = 'total_points'
}) => {
  const [expanded, setExpanded] = useState(false);

  const formatMoney = (amount) => {
    return `$${Number(amount || 0).toFixed(0)}`;
  };

  // Get the display value and label for the current sort key
  const getSortedStatDisplay = () => {
    const statConfig = {
      total_points: { label: 'Points', format: (v) => Number(v || 0).toFixed(0), icon: Trophy },
      current_quota: { label: 'Quota', format: (v) => v || 0, icon: Target },
      hpi_score: { label: 'HPI', format: (v) => Number(v || 0).toFixed(1), icon: Target },
      money_won: { label: 'Money', format: (v) => formatMoney(v), icon: DollarSign },
      skins: { label: 'Skins', format: (v) => v || 0, icon: Target },
      wins: { label: 'Wins', format: (v) => v || 0, icon: Trophy },
      ctps: { label: 'CTPs', format: (v) => v || 0, icon: Target },
      top_3: { label: 'Top 3', format: (v) => v || 0, icon: Trophy },
      top_10: { label: 'Top 10', format: (v) => v || 0, icon: Target },
      events_played: { label: 'Events', format: (v) => v || 0, icon: Target },
      name: { label: 'Points', format: () => Number(player.total_points || 0).toFixed(0), icon: Trophy }
    };

    const config = statConfig[sortKey] || statConfig.total_points;
    const value = sortKey === 'name' ? player.total_points : player[sortKey];

    return {
      value: config.format(value),
      label: config.label,
      Icon: config.icon
    };
  };

  const sortedStat = getSortedStatDisplay();

  // Rank badge styling
  const getRankStyle = () => {
    if (rank === 1) return 'bg-gradient-to-br from-gold-400 to-gold-500 text-forest-950 shadow-lg shadow-gold-400/30 ring-2 ring-gold-300';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-800 shadow-md';
    if (rank === 3) return 'bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-md';
    if (isTop8) return 'bg-gold-400/80 text-forest-950';
    return 'bg-gray-200 dark:bg-navy-700 text-gray-700 dark:text-gray-300';
  };

  return (
    <Card
      className={`
        overflow-hidden transition-all duration-200
        ${isTop8
          ? 'border-l-4 border-l-gold-400 bg-gradient-to-r from-gold-50/50 to-white dark:from-gold-900/10 dark:to-navy-900'
          : 'border-l-4 border-l-transparent'
        }
      `}
    >
      {/* Header Section - Always Visible */}
      <div
        className="flex items-center gap-3 p-4 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Rank Badge with Trend Overlay */}
        <div className="relative flex-shrink-0">
          <div className={`
            flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm
            transition-transform duration-200 hover:scale-110
            ${getRankStyle()}
          `}>
            {rank}
          </div>
          {/* Rank Change Badge */}
          {player.rank_change != null && Number(player.rank_change) !== 0 && (
            <span className={`
              absolute -bottom-1 -right-1
              flex items-center justify-center
              min-w-[18px] h-[18px] px-1
              text-[10px] font-bold rounded-full
              ${Number(player.rank_change) > 0
                ? 'bg-emerald-500 text-white'
                : 'bg-red-500 text-white'
              }
            `}>
              {Number(player.rank_change) > 0 ? '↑' : '↓'}{Math.abs(Number(player.rank_change))}
            </span>
          )}
        </div>

        {/* Player Image */}
        <button
          className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-forest-500 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            onPlayerClick(player);
          }}
          aria-label={`View ${player.name}'s profile`}
        >
          <img
            src={getPlayerImageUrl(player)}
            alt={player.name}
            className={`
              w-12 h-12 rounded-full object-cover shadow-sm
              transition-transform duration-200 hover:scale-105
              ${isTop8
                ? 'ring-2 ring-gold-400 ring-offset-2 dark:ring-offset-navy-900'
                : 'ring-2 ring-white dark:ring-navy-700'
              }
            `}
          />
        </button>

        {/* Name & Quota */}
        <div className="flex-1 min-w-0">
          <h3 className={`
            font-bold truncate
            ${isTop8 ? 'text-forest-900 dark:text-gold-300' : 'text-gray-900 dark:text-gray-100'}
          `}>
            {player.name}
          </h3>
          <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
            Quota: <span className="font-semibold text-gray-700 dark:text-gray-300">{player.current_quota}</span>
          </div>
        </div>

        {/* Primary Stat */}
        <div className="text-right flex-shrink-0">
          <div className={`
            text-xl font-bold tabular-nums
            ${isTop8 ? 'text-gold-600 dark:text-gold-400' : 'text-forest-700 dark:text-forest-400'}
          `}>
            {sortedStat.value}
          </div>
          <div className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
            {sortedStat.label}
          </div>
        </div>

        {/* Expand Toggle */}
        <button
          className="p-2 text-gray-400 hover:text-forest-600 dark:hover:text-forest-400 transition-colors rounded-full hover:bg-gray-100 dark:hover:bg-navy-800"
          aria-expanded={expanded}
          aria-label={expanded ? "Collapse details" : "Expand details"}
        >
          {expanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Expanded Stats Section */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-navy-800 bg-gray-50/50 dark:bg-navy-800/30 p-4">
          {/* Primary Stats Row */}
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center shadow-sm border border-gray-100 dark:border-navy-700">
              <DollarSign className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
              <div className="font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
                {formatMoney(player.money_won)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Money</div>
            </div>
            <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center shadow-sm border border-gray-100 dark:border-navy-700">
              <Target className="w-5 h-5 mx-auto mb-1 text-forest-500" />
              <div className="font-bold text-forest-700 dark:text-forest-400 tabular-nums">
                {Number(player.hpi_score || 0).toFixed(1)}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">HPI</div>
            </div>
            <div className="bg-white dark:bg-navy-800 rounded-xl p-3 text-center shadow-sm border border-gray-100 dark:border-navy-700">
              <Trophy className="w-5 h-5 mx-auto mb-1 text-gold-500" />
              <div className="font-bold text-gray-800 dark:text-gray-200 tabular-nums">
                {player.events_played || 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">Events</div>
            </div>
          </div>

          {/* Secondary Stats Grid */}
          <div className="grid grid-cols-4 gap-2 text-center mb-4">
            <div className="bg-white dark:bg-navy-800 rounded-lg p-2">
              <div className="text-gray-400 dark:text-gray-500 text-xs mb-0.5 font-medium">Wins</div>
              <div className={`font-bold tabular-nums ${(player.wins || 0) > 0 ? 'text-forest-700 dark:text-forest-400' : 'text-gray-400'}`}>
                {player.wins || 0}
              </div>
            </div>
            <div className="bg-white dark:bg-navy-800 rounded-lg p-2">
              <div className="text-gray-400 dark:text-gray-500 text-xs mb-0.5 font-medium">Top 3</div>
              <div className="font-bold text-gray-700 dark:text-gray-300 tabular-nums">{player.top_3 || 0}</div>
            </div>
            <div className="bg-white dark:bg-navy-800 rounded-lg p-2">
              <div className="text-gray-400 dark:text-gray-500 text-xs mb-0.5 font-medium">Skins</div>
              <div className="font-bold text-gray-700 dark:text-gray-300 tabular-nums">{player.skins || 0}</div>
            </div>
            <div className="bg-white dark:bg-navy-800 rounded-lg p-2">
              <div className="text-gray-400 dark:text-gray-500 text-xs mb-0.5 font-medium">CTPs</div>
              <div className="font-bold text-gray-700 dark:text-gray-300 tabular-nums">{player.ctps || 0}</div>
            </div>
          </div>

          {/* View Profile Button */}
          <button
            onClick={() => onPlayerClick(player)}
            className="
              w-full py-3 text-sm font-semibold rounded-xl
              bg-forest-600 hover:bg-forest-700 text-white
              dark:bg-forest-700 dark:hover:bg-forest-600
              transition-colors duration-200
              flex items-center justify-center gap-2
              shadow-sm hover:shadow-md
            "
          >
            <User className="w-4 h-4" />
            View Full Profile
          </button>
        </div>
      )}
    </Card>
  );
};

export default LeaderboardCard;
